// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useEffect } from 'react';

import { useEvolvePaginatedGet, type GetPaginatedOpts } from '@evolvemep/api-ts';
import { Accordion, Stack, Text } from '@mantine/core';

import { PaginatedToggleRadioGroup } from 'components/Mantine/TypeSafeSelect/PaginatedToggleRadioGroup';
import { isNil } from 'helpers/isNotNil';

import { useBendCreatorFormContext } from '../Context/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from '../Context/useBendCreatorValidationContext';
import { BenderProfileOption } from './BenderProfileOption';

export const BendMachineProfileSelect = () => {
  const {
    values: { conduitTypeId, conduitSizeId },
  } = useBendCreatorFormContext();
  const defaultOpts = useMemo<GetPaginatedOpts<'shop/fabrication/machineProfile'>>(
    () => ({
      defaultConfig: {
        params: {
          conduitTypeId: conduitTypeId ?? undefined,
          conduitSizeId: conduitSizeId ?? undefined,
        },
      },
    }),
    [conduitTypeId, conduitSizeId],
  );

  const fetchProfiles = useEvolvePaginatedGet('shop/fabrication/machineProfile');
  const { setDefaultOpts, reset, loading, entireCount } = fetchProfiles;
  useEffect(() => {
    setDefaultOpts(defaultOpts);
    return () => reset();
  }, [setDefaultOpts, defaultOpts, reset]);
  const { fieldGetInputProps, currentProfile } = useBendCreatorValidationContext();

  return (
    <Accordion
      styles={{ root: { backgroundColor: 'var(--mantine-color-gray-1)', borderRadius: 'var(--mantine-radius-md)' } }}
      defaultValue={isNil(currentProfile) ? 'benderProfile' : undefined}
    >
      <Accordion.Item value="benderProfile">
        <Accordion.Control>
          <Text c="dimmed" fz="xs">
            Validation based on this bender:
          </Text>
          <Text fw={600} fz="xs">
            {currentProfile?.machineProfile.name ?? 'Not selected'}
          </Text>
        </Accordion.Control>
        <Accordion.Panel>
          <PaginatedToggleRadioGroup
            paginatedGet={fetchProfiles}
            getOption={(item) => ({
              id: item.machineProfileId,
              value: item.machineProfileId,
              label: (
                <Stack gap={0} flex={1} style={{ fontSize: 'var(--mantine-font-size-sm)', textAlign: 'left' }}>
                  <BenderProfileOption option={{ original: item }} />
                </Stack>
              ),
            })}
            required
            variant="card"
            cols={1}
            {...fieldGetInputProps.benderProfileId()}
          />
          {!loading && entireCount === 0 && (
            <Text c="dimmed" fz="xs">
              No benders available for this conduit type and size.
            </Text>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};
